import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  TrendingUp, TrendingDown, DollarSign, Clock, Target, Sparkles,
  ChevronRight, Info, Zap, BarChart3, AlertCircle, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIPricingSuggestion = ({ 
  category, 
  subcategory, 
  attributes = {}, 
  location = '', 
  condition = '',
  onPriceSelect,
  currentPrice = null
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [selectedPriceType, setSelectedPriceType] = useState('balanced');
  const [saleProbability, setSaleProbability] = useState(null);

  useEffect(() => {
    if (category && subcategory) {
      fetchPricingSuggestion();
    }
  }, [category, subcategory, JSON.stringify(attributes), location, condition]);

  useEffect(() => {
    if (currentPrice && pricingData) {
      fetchSaleProbability(currentPrice);
    }
  }, [currentPrice, pricingData]);

  const fetchPricingSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/ai/pricing/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category,
          subcategory,
          attributes,
          location,
          condition
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to get price suggestion');
      }
      
      const data = await response.json();
      setPricingData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaleProbability = async (price) => {
    try {
      const response = await fetch(`${API_URL}/api/ai/pricing/sale-probability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price: parseFloat(price),
          category,
          subcategory,
          attributes,
          condition
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSaleProbability(data);
      }
    } catch (err) {
      console.error('Error fetching sale probability:', err);
    }
  };

  const handlePriceSelect = (priceType, price) => {
    setSelectedPriceType(priceType);
    if (onPriceSelect) {
      onPriceSelect(price);
    }
    fetchSaleProbability(price);
  };

  const formatPrice = (price) => {
    if (!price) return 'Rs. 0';
    return `Rs. ${price.toLocaleString()}`;
  };

  const getDemandColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDemandBg = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getCompetitivenessColor = (comp) => {
    switch (comp) {
      case 'excellent': return 'text-green-600 bg-green-100';
      case 'good': return 'text-blue-600 bg-blue-100';
      case 'fair': return 'text-yellow-600 bg-yellow-100';
      case 'overpriced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-dashed border-purple-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
            <CardTitle className="text-base">AI Pricing Intelligence</CardTitle>
          </div>
          <CardDescription className="text-xs">Analyzing market data...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-14 col-span-2" />
          </div>
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>Unable to fetch pricing suggestions</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-3"
            onClick={fetchPricingSuggestion}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!pricingData) return null;

  const { suggestions, market_analysis, demand_score, time_to_sell, confidence_level } = pricingData;

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white overflow-hidden">
      <CardHeader className="pb-2 px-3 pt-3 bg-gradient-to-r from-purple-100 to-pink-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <CardTitle className="text-sm text-purple-900">AI Pricing Intelligence</CardTitle>
          </div>
          <Badge variant="outline" className="text-[10px] bg-white px-1.5">
            {confidence_level === 'high' ? '✓ High' : 
             confidence_level === 'medium' ? '~ Medium' : '? Low'}
          </Badge>
        </div>
        <CardDescription className="text-purple-700 text-xs">
          Based on {market_analysis.similar_listings_count} similar listings
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-3 px-3 pb-3 space-y-3">
        {/* Price Suggestions - 2 columns */}
        <div className="grid grid-cols-2 gap-3">
          {/* Quick Sale */}
          <button
            onClick={() => handlePriceSelect('quick_sale', suggestions.quick_sale)}
            className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedPriceType === 'quick_sale' 
                ? 'border-green-500 bg-green-50 shadow-md' 
                : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
            }`}
          >
            <div className="flex items-center gap-1 mb-1">
              <Zap className="w-3 h-3 text-green-600" />
              <span className="text-xs font-medium text-green-700">Quick Sale</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {formatPrice(suggestions.quick_sale)}
            </div>
            <div className="text-xs text-gray-500">
              {suggestions.quick_sale_savings}% below market
            </div>
            <div className="text-xs text-green-600 mt-1 font-medium">
              Sell in ~{Math.max(1, time_to_sell.range.min)} days
            </div>
          </button>

          {/* Balanced */}
          <button
            onClick={() => handlePriceSelect('balanced', suggestions.balanced)}
            className={`p-3 rounded-xl border-2 transition-all duration-200 text-left relative ${
              selectedPriceType === 'balanced' 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
            }`}
          >
            {suggestions.recommended === 'balanced' && (
              <div className="absolute -top-2 right-1">
                <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0">Recommended</Badge>
              </div>
            )}
            <div className="flex items-center gap-1 mb-1">
              <Target className="w-3 h-3 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">Balanced</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {formatPrice(suggestions.balanced)}
            </div>
            <div className="text-xs text-gray-500">
              Market median price
            </div>
            <div className="text-xs text-blue-600 mt-1 font-medium">
              Sell in ~{time_to_sell.estimated_days} days
            </div>
          </button>

          {/* Premium - same size as others */}
          <button
            onClick={() => handlePriceSelect('premium', suggestions.premium)}
            className={`p-3 rounded-xl border-2 transition-all duration-200 text-left relative ${
              selectedPriceType === 'premium' 
                ? 'border-purple-500 bg-purple-50 shadow-md' 
                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
            }`}
          >
            {suggestions.recommended === 'premium' && (
              <div className="absolute -top-2 right-1">
                <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0">Recommended</Badge>
              </div>
            )}
            <div className="flex items-center gap-1 mb-1">
              <TrendingUp className="w-3 h-3 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">Premium</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {formatPrice(suggestions.premium)}
            </div>
            <div className="text-xs text-gray-500">
              +{suggestions.premium_extra}% above market
            </div>
            <div className="text-xs text-purple-600 mt-1 font-medium">
              Sell in ~{time_to_sell.range.max} days
            </div>
          </button>
        </div>

        {/* Demand Score & Market Range - Single column (stacked) */}
        <div className="space-y-3">
          {/* Demand Score */}
          <div className={`p-3 rounded-xl ${getDemandBg(demand_score.score)}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Demand Score</span>
              <div className="flex items-center gap-1">
                {demand_score.trend === 'increasing' ? (
                  <TrendingUp className="w-4 h-4 text-green-600" />
                ) : demand_score.trend === 'decreasing' ? (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                ) : null}
                <span className={`text-lg font-bold ${getDemandColor(demand_score.score)}`}>
                  {demand_score.score}/100
                </span>
              </div>
            </div>
            <Progress value={demand_score.score} className="h-2" />
            <p className="text-xs text-gray-600 mt-2">
              {demand_score.score >= 70 ? 'High demand - items sell quickly!' :
               demand_score.score >= 40 ? 'Moderate demand - standard selling time' :
               'Lower demand - consider competitive pricing'}
            </p>
          </div>

          {/* Market Price Range */}
          <div className="p-3 rounded-xl bg-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Market Range</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Min</span>
              <span className="text-gray-500">Median</span>
              <span className="text-gray-500">Max</span>
            </div>
            <div className="flex items-center justify-between text-sm font-medium mt-1">
              <span className="text-gray-700">{formatPrice(market_analysis.price_range.min)}</span>
              <span className="text-blue-600 font-bold">{formatPrice(market_analysis.price_range.median)}</span>
              <span className="text-gray-700">{formatPrice(market_analysis.price_range.max)}</span>
            </div>
            <div className="mt-2 h-2 bg-gradient-to-r from-green-300 via-blue-400 to-purple-400 rounded-full" />
          </div>
        </div>

        {/* Sale Probability (if price is set) */}
        {saleProbability && (
          <div className="p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Sale Probability</span>
              </div>
              <Badge className={getCompetitivenessColor(saleProbability.price_analysis?.competitiveness)}>
                {saleProbability.price_analysis?.competitiveness || 'N/A'}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xl font-bold text-blue-600">
                  {saleProbability.probabilities?.['7_days']}%
                </div>
                <div className="text-xs text-gray-500">7 Days</div>
              </div>
              <div>
                <div className="text-xl font-bold text-indigo-600">
                  {saleProbability.probabilities?.['14_days']}%
                </div>
                <div className="text-xs text-gray-500">14 Days</div>
              </div>
              <div>
                <div className="text-xl font-bold text-purple-600">
                  {saleProbability.probabilities?.['30_days']}%
                </div>
                <div className="text-xs text-gray-500">30 Days</div>
              </div>
            </div>
            
            {/* Improvement Suggestions */}
            {saleProbability.improvement_suggestions && saleProbability.improvement_suggestions.length > 0 && (
              <div className="mt-3 pt-2 border-t border-blue-200">
                <span className="text-xs font-medium text-gray-600">💡 Tips to improve:</span>
                <div className="mt-1 space-y-1">
                  {saleProbability.improvement_suggestions.slice(0, 2).map((tip, idx) => (
                    <div key={idx} className="flex items-center gap-1 text-xs text-gray-600">
                      <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                      <span className="truncate">{tip.action}</span>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{tip.impact}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Use Price Button */}
        <Button 
          size="sm"
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-sm"
          onClick={() => {
            const price = suggestions[selectedPriceType];
            if (onPriceSelect) {
              onPriceSelect(price);
            }
          }}
        >
          <DollarSign className="w-3 h-3 mr-1" />
          Use {formatPrice(suggestions[selectedPriceType])}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AIPricingSuggestion;
